/**
 * Document Style Provider – font & layout utilities
 *
 * There are two font-injection paths because the editor and the print iframe
 * have fundamentally different timing constraints:
 *
 * Editor (injectFontLink / applyDocumentStyleGuide)
 *   Appends a <link rel="stylesheet"> to the *main* document's <head>.
 *   The browser fetches and parses the external CSS asynchronously.
 *   This is fine for the editor because DocumentStyleProvider waits for
 *   document.fonts.load() before setting fontsReady — the document is not
 *   shown until the font is available.
 *
 * Print iframe (injectPrintFonts)
 *   The print iframe is a short-lived clone.  Although it inherits the
 *   <link> stylesheet from the main document, the external CSS may not be
 *   parsed and the .ttf files may not be fetched by the time print() is
 *   called.  If the font is missing, the browser falls back to a system
 *   font with different character metrics — paragraphs render at different
 *   heights, causing page breaks to misalign between screen and PDF.
 *
 *   To guarantee the font is ready, injectPrintFonts writes inline
 *   @font-face rules (no external CSS fetch) and awaits the FontFace
 *   Loading API.  The .ttf files are served from browser cache since
 *   the main document already loaded them.
 */
import {
  DOCUMENT_FONT_LINK_IDS,
  STYLE_GUIDE_FONT_MAP,
  FONT_FACES,
} from './DocumentStyleProvider.constants';
import {PAGE_NUMBER_ALIGN_ITEMS_MAP} from '../../constants';

export function injectFontLink(id, href) {
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

export function removeFontLink(id) {
  document.getElementById(id)?.remove();
}

/**
 * Applies document style guide to the root. For configured hosted webfonts,
 * injects the stylesheet link but does NOT set --document-font-family yet
 * (caller sets it after font is loaded to avoid a flash from fallback to
 * webfont). Returns what to load and the value to set when ready.
 * @returns {{ fontToLoad: string | null, familyValue: string | null }}
 */
export function applyDocumentStyleGuide(root, styleConfig) {
  const {
    fontFamily,
    pageMarginTopBottom,
    pageMarginLeftRight,
    lineHeight,
    listItemSpacing,
    pageNumberAlignment,
  } = styleConfig ?? {};

  let fontToLoad = null;
  let familyValue = null;

  if (fontFamily?.trim()) {
    const trimmed = fontFamily.trim();
    const mapping = STYLE_GUIDE_FONT_MAP[trimmed];
    if (mapping) {
      injectFontLink(mapping.linkId, mapping.href);
      fontToLoad = mapping.fontFaceName;
      familyValue = mapping.familyValue;
    } else {
      root.style.setProperty('--document-font-family', trimmed);
    }
  }
  /* No fontFamily in config: leave --document-font-family unset; editor CSS uses
   * var(--document-font-family, 'Times New Roman', Times, serif). */

  if (pageMarginTopBottom) {
    root.style.setProperty('--page-margin-top-bottom', pageMarginTopBottom);
  }
  if (pageMarginLeftRight) {
    root.style.setProperty('--page-margin-left-right', pageMarginLeftRight);
  }
  if (lineHeight) {
    root.style.setProperty('--line-height', lineHeight);
  }
  if (listItemSpacing) {
    root.style.setProperty('--list-item-spacing', listItemSpacing);
  }

  if (pageNumberAlignment) {
    root.style.setProperty('--page-number-alignment', pageNumberAlignment);
    root.style.setProperty(
      '--page-number-align-items',
      PAGE_NUMBER_ALIGN_ITEMS_MAP[pageNumberAlignment] ?? 'center'
    );
  }

  return {fontToLoad, familyValue};
}

/** Remove all style-guide CSS custom properties and font stylesheet links. */
export function clearDocumentStyleGuide(root) {
  root.style.removeProperty('--document-font-family');
  root.style.removeProperty('--page-margin-top-bottom');
  root.style.removeProperty('--page-margin-left-right');
  root.style.removeProperty('--line-height');
  root.style.removeProperty('--list-item-spacing');
  root.style.removeProperty('--page-number-alignment');
  root.style.removeProperty('--page-number-align-items');
  DOCUMENT_FONT_LINK_IDS.forEach(removeFontLink);
}

function resolveFont(styleGuide) {
  const family = styleGuide?.fontFamily?.trim();
  if (!family) return null;
  return STYLE_GUIDE_FONT_MAP[family] ?? null;
}

/**
 * Inject @font-face declarations and load the correct webfont in a print
 * iframe based on the active style guide.
 *
 * When the style guide specifies a font that maps to a configured hosted
 * webfont, the matching @font-face rules are injected and explicitly loaded
 * via the FontFace Loading API.  When no webfont is
 * needed (e.g. a system font), this is a no-op.
 *
 * @param {Document} iframeDoc  - The print iframe's document
 * @param {Object}   [styleGuide] - The org's documentStyleGuide config
 * @returns {Promise<void>}
 */
export async function injectPrintFonts(iframeDoc, styleGuide) {
  const font = resolveFont(styleGuide);
  if (!font) return;

  const faces = FONT_FACES[font.fontFaceName];
  if (!faces?.length) return;

  const css = faces
    .map(
      (f) =>
        `@font-face { font-family: '${font.fontFaceName}'; font-weight: ${f.weight}; font-style: ${f.style}; src: url('${f.src}') format('truetype'); }`
    )
    .join('\n');

  const style = iframeDoc.createElement('style');
  style.id = 'print-font-inject';
  style.textContent = css;
  iframeDoc.head.insertBefore(style, iframeDoc.head.firstChild);

  try {
    await Promise.all(
      faces.map((f) =>
        iframeDoc.fonts.load(
          `${f.style === 'italic' ? 'italic ' : ''}${f.weight} 1em "${font.fontFaceName}"`
        )
      )
    );
  } catch (e) {
    console.warn('Font loading in print iframe failed:', e);
  }
}
